"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { track } from "@vercel/analytics";
import { useLocale } from "@/lib/i18n";
import { localeHref } from "@/lib/i18n/config";
import { submitContact } from "@/app/actions/submit-contact";
import type { ContactInput } from "@/lib/schema";
import { Button } from "@/components/ui/Button";

const inputClass =
  "w-full rounded-md border border-[var(--color-hairline)] bg-[color:rgba(255,255,255,0.03)] px-4 py-3 text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-blue)] focus:bg-[color:rgba(59,130,246,0.06)]";

function Label({ htmlFor, children, optional }: { htmlFor: string; children: React.ReactNode; optional?: string }) {
  return (
    <label htmlFor={htmlFor} className="microcaps mb-2 block text-[var(--color-text-dim)]">
      {children}
      {optional ? <span className="ml-1 opacity-60">· {optional}</span> : null}
    </label>
  );
}

/**
 * Standalone /contact form. Trilingual via useLocale(); posts to the
 * submitContact server action (shared store + delivery + spam protection). The
 * hidden company_url field is a honeypot. Submissions store + succeed even
 * before email delivery is provisioned (the action degrades gracefully).
 */
export function ContactForm() {
  const { t, locale } = useLocale();
  const lf = t.leadForm;

  const nameR = useRef<HTMLInputElement>(null);
  const emailR = useRef<HTMLInputElement>(null);
  const companyR = useRef<HTMLInputElement>(null);
  const typeR = useRef<HTMLSelectElement>(null);
  const budgetR = useRef<HTMLSelectElement>(null);
  const messageR = useRef<HTMLTextAreaElement>(null);
  const hpR = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<"idle" | "submitting">("idle");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setError("");

    const raw: ContactInput = {
      name: nameR.current?.value.trim() ?? "",
      email: emailR.current?.value.trim() ?? "",
      company: companyR.current?.value.trim() || undefined,
      projectType: (typeR.current?.value ?? "web-app") as ContactInput["projectType"],
      budget: (budgetR.current?.value ?? "unsure") as ContactInput["budget"],
      message: messageR.current?.value.trim() ?? "",
    };

    if (!raw.name) { setError(lf.errors.nameRequired); nameR.current?.focus(); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw.email)) { setError(lf.errors.emailInvalid); emailR.current?.focus(); return; }
    if (raw.message.length < 20) { setError(lf.errors.ideaShort); messageR.current?.focus(); return; }

    setStatus("submitting");
    const res = await submitContact(raw, hpR.current?.value ?? "");
    setStatus("idle");
    if (res.ok) {
      track("contact_submitted", { source: "contact_page", projectType: raw.projectType });
      setDone(true);
    } else setError(res.error || lf.errors.generic);
  }

  if (done) {
    return (
      <div role="status" aria-live="polite" className="rounded-lg border border-[var(--color-hairline)] bg-[color:rgba(59,130,246,0.06)] p-8 text-center">
        <div
          aria-hidden
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[var(--color-blue)] text-2xl text-[var(--color-blue)]"
        >
          ✓
        </div>
        <h2
          className="mb-3 text-2xl lg:text-3xl text-[var(--color-text)]"
          style={{ fontFamily: "var(--font-display)", fontVariationSettings: '"wght" 600' }}
        >
          {t.cta.receivedTitle}
        </h2>
        <p className="mx-auto max-w-[420px] text-[var(--color-text-dim)]">{t.cta.receivedBody}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      {/* honeypot — visually hidden; bots that fill every field trip it */}
      <input
        ref={hpR}
        type="text"
        name="company_url"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute -left-[9999px] h-px w-px overflow-hidden"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="cf-name">{lf.name}</Label>
          <input ref={nameR} id="cf-name" name="name" type="text" autoComplete="name" placeholder={lf.namePlaceholder} aria-describedby="cf-error" className={inputClass} />
        </div>
        <div>
          <Label htmlFor="cf-email">{lf.email}</Label>
          <input ref={emailR} id="cf-email" name="email" type="email" autoComplete="email" placeholder={lf.emailPlaceholder} aria-describedby="cf-error" className={inputClass} />
        </div>
        <div>
          <Label htmlFor="cf-company" optional={lf.companyOptional}>{lf.company}</Label>
          <input ref={companyR} id="cf-company" name="company" type="text" autoComplete="organization" placeholder={lf.companyPlaceholder} aria-describedby="cf-error" className={inputClass} />
        </div>
        <div>
          <Label htmlFor="cf-type">{lf.projectType}</Label>
          <select ref={typeR} id="cf-type" name="projectType" defaultValue="web-app" aria-describedby="cf-error" className={`${inputClass} cursor-pointer appearance-none`}>
            {lf.projectTypes.map((o) => (
              <option key={o.value} value={o.value} className="bg-[#0d1d31]">{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label htmlFor="cf-budget">{lf.budget}</Label>
        <select ref={budgetR} id="cf-budget" name="budget" defaultValue="unsure" aria-describedby="cf-error" className={`${inputClass} cursor-pointer appearance-none`}>
          {lf.budgetOptions.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#0d1d31]">{o.label}</option>
          ))}
        </select>
      </div>

      <div>
        <Label htmlFor="cf-message">{lf.idea}</Label>
        <textarea ref={messageR} id="cf-message" name="message" rows={5} placeholder={lf.ideaPlaceholder} aria-describedby="cf-error" className={`${inputClass} resize-y leading-relaxed`} />
      </div>

      <div id="cf-error" role="alert" aria-live="assertive" className="min-h-[20px] text-sm text-[#f0a5a5]">
        {error}
      </div>

      <Button type="submit" variant="primary" trailingArrow disabled={status === "submitting"} className="w-full justify-center sm:w-auto">
        {status === "submitting" ? lf.submitting : lf.submit}
      </Button>

      <p className="text-sm text-[var(--color-text-mute)]">
        {lf.consent}{" "}
        <Link href={localeHref(locale, "/privacy")} className="link-underline text-[var(--color-text-dim)] hover:text-[var(--color-text)]">
          {lf.privacyPolicy}
        </Link>
        .
      </p>
    </form>
  );
}
